import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { Link } from "react-router";
import { MotionConfig, motion } from "framer-motion";
import { ArrowRight, BarChart3, CalendarPlus, Check, Heart, Lock, MessageCircle, PenLine, Pin, Play, Radio, Sparkles, Upload, Youtube } from "lucide-react";
import { livesInMonth, upcomingLives, YOUTUBE_LIVE_URL, type Live } from "@/lib/schedule";
import { Draw, EASE, HEART, Head, Ink, VIEW, datePart } from "../bits";
import { Part } from "../Page";
import { JoinButton, Locked, LockedRow, MembersPage, M, VIS_LABEL, VisBadge, hasAccess, joinCta, useRole, useTo } from "./Members";
import { CHALLENGE, CHELSEA, CLASSES, FOUNDING, LIVE_MS, MAKES, PLANS, POSTS, PROJECTS, byNewest, can, classStart, countComments, hasReplay, replays, viewer, type ClassVideo, type Post, type Role, type Viewer } from "./sample";
import "../SectionsA.css";
import "./Home.css";

/* Club home (/blue-2/members): the member's dashboard. Next live class with a
   countdown, this month's project, replays to finish, the latest posts, makes
   and the challenge, and the free YouTube Tuesdays. Visitors and Thread Friends
   see the same rooms with tasteful locks; Chelsea gets a studio desk (post,
   schedule a live, upload a replay) and a few sample numbers. Prototype: every
   interaction is local state, nothing is sent anywhere. */

/* ─── Extra sample data (would live in sample.ts) ─── */
const STATS = [
  { n: "412", label: "members" },
  { n: "+9", label: "new this week" },
  { n: "138", label: "Thread Friends" },
  { n: "61", label: "RSVPs, next class" },
];
// Views only exist for classes that have aired (see hasReplay in sample.ts).
const REPLAY_VIEWS: Record<string, number> = { "vest-1": 388, "vest-2": 301, "pouch-1": 452 };
const TAPES = ["soft", "pop", "extra", "soft", "pop", "extra"] as const;

/* ─── Small helpers ─── */
function useNow(ms = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = window.setInterval(() => setNow(new Date()), ms); return () => window.clearInterval(t); }, [ms]);
  return now;
}

function useToast() {
  const [toast, setToast] = useState<{ msg: string; n: number } | null>(null);
  useEffect(() => { if (!toast) return; const t = window.setTimeout(() => setToast(null), 3600); return () => window.clearTimeout(t); }, [toast]);
  return { toast, show: (msg: string) => setToast(t => ({ msg, n: (t?.n ?? 0) + 1 })) };
}

function Toast({ toast }: { toast: { msg: string; n: number } | null }) {
  return <div className="mh-toast-wrap" role="status" aria-live="polite">
    {toast && <motion.p key={toast.n} className="mh-toast" initial={{ opacity: 0, y: 16, scale: .96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: .3, ease: EASE }}>
      <Sparkles size={16} aria-hidden="true" /> {toast.msg}
    </motion.p>}
  </div>;
}

const sameDay = (a: Date, b: Date) => datePart(a, { dateStyle: "short" }) === datePart(b, { dateStyle: "short" });
const dayWord = (d: Date, now: Date) => {
  if (sameDay(d, now)) return "tonight";
  if (sameDay(d, new Date(now.getTime() + 864e5))) return "tomorrow";
  if (d.getTime() - now.getTime() < 6 * 864e5) return datePart(d, { weekday: "long" });
  return datePart(d, { month: "short", day: "numeric" });
};
const longDate = (d: Date) => datePart(d, { weekday: "long", month: "long", day: "numeric" });
const isoDenver = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: "America/Denver" });
/* Class timing (classStart, hasReplay, replays, byNewest, LIVE_MS) lives in
   sample.ts, so tonight's class never shows up as a replay anywhere. */
/** Where a club live sits in its month: class 1 of 2, and both nights. */
function classNights(live: Live) {
  const y = Number(datePart(live.start, { year: "numeric" })), m = Number(datePart(live.start, { month: "numeric" })) - 1;
  const nights = livesInMonth(y, m).filter(l => l.kind === "club");
  return { nights, n: nights.findIndex(l => l.start.getTime() === live.start.getTime()) + 1 };
}

const secs = (t: string) => t.split(":").map(Number).reduce((a, n) => a * 60 + n, 0);
const clock = (s: number) => { const h = Math.floor(s / 3600), m = Math.floor(s % 3600 / 60), x = Math.floor(s % 60);
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(x).padStart(2, "0")}` : `${m}:${String(x).padStart(2, "0")}`; };

/** Which tier a post needs (null for public posts). */
const postNeed = (p: Post) => (p.visibility === "public" ? null : p.visibility === "friends" ? "friends" as const : "members" as const);
/** Who can read a post: public is everyone, friends is Thread Friends and up, members is members. */
const canRead = (role: Role, p: Post) => { const need = postNeed(p); return !need || hasAccess(role, need); };
// Locked posts never ship their real text; the blur is filler.
const LOCKED_FILLER = "A little note from the studio, just for the people inside the club. Grab a cup of tea and come read it with us.";

function Avatar({ name, src, size = 40 }: { name: string; src?: string; size?: number }) {
  return src ? <img className="mh-avatar" src={src} alt="" width={size} height={size} style={{ width: size, height: size }} />
    : <span className="mh-avatar mh-avatar-initials" aria-hidden="true" style={{ width: size, height: size, fontSize: size * .4 }}>{name.split(" ").map(w => w[0]).join("").slice(0, 2)}</span>;
}

/* ═══ Header ═══ */
type Desk = "post" | "live" | "replay" | null;

function Header({ openDesk }: { openDesk: (d: Desk) => void }) {
  const role = useRole();
  const now = useNow(30000);
  const nextClub = upcomingLives(1, "club", now)[0] as Live | undefined;
  const to = useTo();
  const project = PROJECTS[0];
  const when = nextClub ? dayWord(nextClub.start, now) : "";
  const thisProject = !!nextClub && datePart(nextClub.start, { month: "long", year: "numeric" }) === project.month;
  const n = nextClub ? classNights(nextClub).n : 0;
  const tonight = when === "tonight";
  const today = datePart(now, { weekday: "long", month: "long", day: "numeric" });
  const copy = {
    member: { kicker: `Loose Threads Club · ${today}`, hand: "welcome back,", word: (viewer("member") as Viewer).first,
      lede: !nextClub ? <>Your replays and the latest from the studio are all right here.</>
        : thisProject ? <>{project.title} class {n} is <b>{tonight ? "tonight at 7 PM MT" : `${longDate(nextClub.start)}, 7 PM MT`}</b>. Fabric pulled? Bobbins wound? I’ll save you a seat.</>
        : <>The next club class is <b>{longDate(nextClub.start)}, 7 PM MT</b>. Next month’s project drops soon, and every replay is waiting in the meantime.</> },
    chelsea: { kicker: `Creator view · ${today}`, hand: "hi,", word: "Chelsea",
      lede: <>Here’s the club at a glance. {nextClub && tonight ? <>You’re on <b>tonight at 7</b> for {thisProject ? `${project.title} class ${n}` : "club class"}. </> : null}Go live, post an update, or drop in a replay from the studio desk.</> },
    visitor: { kicker: "Loose Threads Club · sew with Chelsea", hand: "come sew in the", word: "club",
      lede: <>Sew along with me live twice a month, catch every replay, and share your makes with the nicest sewists on the internet. Right here, no extra app.</> },
    friend: { kicker: "Thread Friend · thank you ♥", hand: "hi, thread", word: "friend",
      lede: <>Thank you for keeping the lights on! Club posts and studio updates are all yours. Want to sew with me live, too?</> },
  }[role];
  const actions = {
    member: <>
      <Link to={to(M.live)} className="mb-btn mb-btn-pop"><Radio size={18} aria-hidden="true" /> {tonight ? "Tonight’s live room" : "The live room"}</Link>
      <Link to={to(M.klass(CLASSES.find(c => c.projectId === project.id)?.id ?? CLASSES[0].id))} className="mb-btn mb-btn-line">This month’s project</Link>
    </>,
    chelsea: <>
      <Link to={to(M.live)} className="mb-btn mb-btn-pop"><Radio size={18} aria-hidden="true" /> Open the studio</Link>
      <button type="button" className="mb-btn mb-btn-line" onClick={() => openDesk("post")}><PenLine size={17} aria-hidden="true" /> New post</button>
    </>,
    visitor: <>
      <JoinButton variant="pop" secondary={false} />
      <a href="#mh-upnext" className="mb-btn mb-btn-line">Take a peek inside</a>
    </>,
    friend: <>
      <JoinButton variant="pop" />
      <Link to={to(M.posts)} className="mb-btn mb-btn-line">Read the latest posts</Link>
    </>,
  }[role];
  const word = copy.word;

  return <div className="mh-head">
    <div className="mh-head-copy">
      <motion.p className="mh-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .1 }}>{copy.kicker}</motion.p>
      <h1 className="mh-title" aria-label={`${copy.hand} ${word}`}>
        <motion.span className="mh-title-hand" aria-hidden="true" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: .35, duration: .5, ease: EASE }}>{copy.hand}</motion.span>
        <span className="mh-title-word" aria-hidden="true" style={{ "--mh-len": Math.max(word.length, 3) } as CSSProperties}>
          {word.split("").map((c, i) => <motion.span key={i} initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: .1 + i * .05, duration: .6, ease: EASE }}>{c}</motion.span>)}
        </span>
      </h1>
      <motion.p className="mh-lede" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .6, duration: .45, ease: EASE }}>{copy.lede}</motion.p>
      <motion.div className="mh-head-actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .75, duration: .45, ease: EASE }}>{actions}</motion.div>
    </div>
    <div className="mh-head-side">
      {role === "member" && <MemberCard who={viewer("member")!} note={nextClub ? `next class: ${when}, 7 PM` : "every replay, any time"} />}
      {role === "friend" && <MemberCard who={viewer("friend")!} note="you keep the free lives going ♥" />}
      {role === "chelsea" && <HeadStats />}
      {role === "visitor" && <VisitorSnap />}
    </div>
  </div>;
}

function MemberCard({ who, note }: { who: Viewer; note: string }) {
  const plan = PLANS[who.plan];
  return <motion.div className="mh-card" initial={{ opacity: 0, y: 30, rotate: 8 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ delay: .4, type: "spring", stiffness: 120, damping: 15 }}>
    <i className="b2-snap-tape b2-ktape-soft" aria-hidden="true" />
    <p className="mh-card-club">Loose Threads Club</p>
    <div className="mh-card-who">
      <Avatar name={who.name} size={58} />
      <div>
        <p className="mh-card-name">{who.name}</p>
        <p className="mh-card-tier">{plan.name} · {plan.per}</p>
      </div>
    </div>
    {who.founding ? <p className="mh-card-badge"><Sparkles size={13} aria-hidden="true" /> {FOUNDING} · since {who.since}</p>
      : <p className="mh-card-badge"><Heart size={13} aria-hidden="true" /> In the club since {who.since}</p>}
    <p className="mh-card-note">{note}</p>
    <svg className="mh-card-heart" viewBox="0 0 30 46" aria-hidden="true"><Draw d={HEART} delay={1} width={3.4} /></svg>
  </motion.div>;
}

function HeadStats() {
  return <div className="mh-hstats">
    <ul aria-label="Club at a glance (sample numbers)">
      {STATS.map((s, i) => <motion.li key={s.label} initial={{ opacity: 0, y: 24, rotate: 0 }} animate={{ opacity: 1, y: 0, rotate: [-3, 2, 1.5, -2][i] }} transition={{ delay: .4 + i * .1, type: "spring", stiffness: 140, damping: 15 }}>
        <b>{s.n}</b><span>{s.label}</span>
      </motion.li>)}
    </ul>
    <p className="mh-hstats-note">sample numbers, for layout</p>
  </div>;
}

function VisitorSnap() {
  return <div className="mh-vsnap">
    <motion.p className="mh-price" initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: -12 }} transition={{ delay: 1, type: "spring", stiffness: 220, damping: 13 }}>
      <b>$25</b>a month<small>cancel anytime</small>
    </motion.p>
    <motion.figure className="mh-polaroid" initial={{ opacity: 0, y: 30, rotate: 8 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ delay: .4, type: "spring", stiffness: 120, damping: 15 }}>
      <i className="b2-snap-tape b2-ktape-soft" aria-hidden="true" />
      <span><img src="/chelsea-and-cosmo.jpg" alt="Cosmo the cat on Chelsea’s lap at the sewing machine" /></span>
      <figcaption>Cosmo never misses a class <b aria-hidden="true">♥</b></figcaption>
    </motion.figure>
  </div>;
}

/* ═══ Studio desk (Chelsea) ═══ */
type Scheduled = { date: string; kind: "club" | "youtube"; title: string };

type Defaults = { club: string; youtube: string; today: string };

function StudioDesk({ open, setOpen, onPost, onPublish, toast, defaults, now }: { open: Desk; setOpen: (d: Desk) => void; onPost: (p: Post) => void; onPublish: (id: string) => void; toast: (m: string) => void; defaults: Defaults; now: Date }) {
  const ref = useRef<HTMLElement>(null);
  const firstOpen = useRef(true);
  useEffect(() => {
    if (firstOpen.current) { firstOpen.current = false; return; }
    if (open) ref.current?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  }, [open]);
  const tools = [
    { key: "post" as const, icon: <PenLine size={22} aria-hidden="true" />, title: "New post", sub: "an update for the club" },
    { key: "live" as const, icon: <CalendarPlus size={22} aria-hidden="true" />, title: "Schedule a live", sub: "club class or YouTube" },
    { key: "replay" as const, icon: <Upload size={22} aria-hidden="true" />, title: "Upload a replay", sub: "from OBS, chapters later" },
  ];
  return <section ref={ref} className="mh-sec mh-desk" aria-labelledby="mh-desk-title">
    <div className="b2-wrap">
      <div className="mh-desk-row">
        <h2 id="mh-desk-title" className="mh-desk-title"><span>studio</span> desk</h2>
        <p className="mh-desk-hint">Only you see this. Everything here stays on this page (prototype).</p>
      </div>
      <div className="mh-tools" role="group" aria-label="Quick actions">
        {tools.map((t, i) => <button key={t.key} type="button" className={`mh-tool mh-tool-${i}`} aria-expanded={open === t.key} aria-controls={`mh-desk-${t.key}`} onClick={() => setOpen(open === t.key ? null : t.key)}>
          <span className="mh-tool-icon">{t.icon}</span>
          <span><b>{t.title}</b><small>{t.sub}</small></span>
        </button>)}
      </div>
      {open === "post" && <Composer onPost={p => { onPost(p); setOpen(null); toast("Posted! It’s at the top of the latest posts (this page only)."); }} />}
      {open === "live" && <ScheduleLive toast={toast} defaults={defaults} />}
      {open === "replay" && <UploadReplay toast={toast} onPublish={onPublish} now={now} />}
    </div>
  </section>;
}

function Composer({ onPost }: { onPost: (p: Post) => void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [vis, setVis] = useState<Post["visibility"]>("members");
  const id = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onPost({ id: `new-${Date.now()}`, kind: "update", visibility: vis, title: title.trim(), body: body.trim(), ago: "Just now", likes: 0, comments: [] });
  };
  return <motion.form id="mh-desk-post" className="mh-panel" onSubmit={submit} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .3, ease: EASE }}>
    <div className="mh-field">
      <label htmlFor={`${id}-t`}>Title</label>
      <input id={`${id}-t`} value={title} onChange={e => setTitle(e.target.value)} placeholder="Supply list is up for November!" required autoFocus />
    </div>
    <div className="mh-field">
      <label htmlFor={`${id}-b`}>What’s new?</label>
      <textarea id={`${id}-b`} rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder="Tell the club…" />
    </div>
    <div className="mh-panel-foot">
      <div className="mh-field mh-field-inline">
        <label htmlFor={`${id}-v`}>Who can see it</label>
        <select id={`${id}-v`} value={vis} onChange={e => setVis(e.target.value as Post["visibility"])}>
          {(["members", "friends", "public"] as const).map(v => <option key={v} value={v}>{VIS_LABEL[v].who}</option>)}
        </select>
      </div>
      <button type="submit" className="mb-btn mb-btn-ink" disabled={!title.trim()}>Post it <ArrowRight size={16} aria-hidden="true" /></button>
    </div>
  </motion.form>;
}

function ScheduleLive({ toast, defaults }: { toast: (m: string) => void; defaults: Defaults }) {
  const [kind, setKind] = useState<Scheduled["kind"]>("club");
  const [date, setDate] = useState(defaults.club);
  // Switching where it streams suggests that kind's next open night.
  const pickKind = (k: Scheduled["kind"]) => { setKind(k); setDate(defaults[k]); };
  const [title, setTitle] = useState("");
  const [list, setList] = useState<Scheduled[]>([]);
  const id = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!date) return;
    const t = title.trim() || (kind === "club" ? "Club class" : "Free YouTube live");
    setList(l => [...l, { date, kind, title: t }]);
    setTitle("");
    const d = new Date(`${date}T12:00:00`);
    toast(`Scheduled: ${t}, ${d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at 7 PM MT.`);
  };
  return <motion.form id="mh-desk-live" className="mh-panel" onSubmit={submit} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .3, ease: EASE }}>
    <fieldset className="mh-seg">
      <legend>Where</legend>
      <label><input type="radio" name={`${id}-k`} checked={kind === "club"} onChange={() => pickKind("club")} /><span><Lock size={14} aria-hidden="true" /> Club class (members)</span></label>
      <label><input type="radio" name={`${id}-k`} checked={kind === "youtube"} onChange={() => pickKind("youtube")} /><span><Youtube size={15} aria-hidden="true" /> YouTube (free)</span></label>
    </fieldset>
    <div className="mh-panel-grid">
      <div className="mh-field"><label htmlFor={`${id}-d`}>Date</label><input id={`${id}-d`} type="date" min={defaults.today} value={date} onChange={e => setDate(e.target.value)} required /></div>
      <div className="mh-field"><label htmlFor={`${id}-n`}>Title (optional)</label><input id={`${id}-n`} value={title} onChange={e => setTitle(e.target.value)} placeholder={kind === "club" ? "Flannel PJs, class 1" : "Fabric haul + Q&A"} /></div>
    </div>
    <div className="mh-panel-foot">
      <p className="mh-panel-note">7 PM Mountain, like always. OBS streams to the club’s private room for club classes.</p>
      <button type="submit" className="mb-btn mb-btn-ink">Schedule it <ArrowRight size={16} aria-hidden="true" /></button>
    </div>
    {list.length > 0 && <ul className="mh-scheduled" aria-label="Scheduled on this page">
      {list.map((s, i) => <li key={i}><Check size={15} aria-hidden="true" /> {new Date(`${s.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} · {s.title} <em>{s.kind === "club" ? "Club" : "YouTube"}</em></li>)}
    </ul>}
  </motion.form>;
}

function UploadReplay({ toast, onPublish, now }: { toast: (m: string) => void; onPublish: (id: string) => void; now: Date }) {
  // Classes that have started by tonight; today's class comes first.
  const options = useMemo(() => CLASSES.filter(c => isoDenver(classStart(c)) <= isoDenver(now)).sort(byNewest), [now]);
  const [klass, setKlass] = useState(options[0]?.id ?? CLASSES[0].id);
  const [file, setFile] = useState<string | null>(null);
  const [pct, setPct] = useState(0);
  const [done, setDone] = useState(false);
  const id = useId();
  useEffect(() => {
    if (!file || pct >= 100) return;
    const t = window.setTimeout(() => setPct(p => Math.min(100, p + 4 + Math.round(Math.random() * 6))), 90);
    return () => window.clearTimeout(t);
  }, [file, pct]);
  const start = (name: string) => { setFile(name); setPct(0); setDone(false); };
  const c = CLASSES.find(x => x.id === klass)!;
  return <motion.div id="mh-desk-replay" className="mh-panel" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .3, ease: EASE }}>
    <div className="mh-panel-grid">
      <div className="mh-field"><label htmlFor={`${id}-c`}>Which class</label>
        <select id={`${id}-c`} value={klass} onChange={e => { setKlass(e.target.value); setFile(null); setPct(0); setDone(false); }}>
          {options.map(x => <option key={x.id} value={x.id}>{x.title} ({x.part}){!hasReplay(x, now) ? " · tonight" : REPLAY_VIEWS[x.id] ? " · replace replay" : ""}</option>)}
        </select>
      </div>
      <div className="mh-field"><span className="mh-label" id={`${id}-fl`}>Recording</span>
        <div className="mh-drop">
          <label className="mh-drop-pick"><input type="file" accept="video/*" aria-labelledby={`${id}-fl`} onChange={e => { const f = e.target.files?.[0]; if (f) start(f.name); }} /><Upload size={16} aria-hidden="true" /> Choose a video</label>
          <button type="button" className="mh-linkbtn" onClick={() => start(`${c.id}-obs-recording.mp4`)}>or try a sample file</button>
        </div>
      </div>
    </div>
    {file && <div className="mh-upload" aria-live="polite">
      <p className="mh-upload-name"><b>{file}</b> <span>{pct < 100 ? `Uploading… ${pct}%` : done ? "Published to members" : "Uploaded. Ready to publish."}</span></p>
      <div className="mh-bar" role="progressbar" aria-label="Upload progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}><i style={{ width: `${pct}%` }} /></div>
      {pct >= 100 && !done && <div className="mh-panel-foot">
        <p className="mh-panel-note">Chapters from the class plan: {c.chapters.length} found. You can edit them on the class page.</p>
        <button type="button" className="mb-btn mb-btn-ink" onClick={() => { setDone(true); onPublish(c.id); toast(`Replay published: ${c.title}. Members get a heads-up.`); }}>Publish replay <ArrowRight size={16} aria-hidden="true" /></button>
      </div>}
    </div>}
  </motion.div>;
}

/* ═══ Friend: what you get ═══ */
function FriendNudge() {
  return <section className="mh-sec mh-nudge" aria-labelledby="mh-nudge-title">
    <div className="b2-wrap">
      <motion.div className="mh-nudge-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: .5, ease: EASE }}>
        <h2 id="mh-nudge-title" className="mh-nudge-title">your Thread Friend perks</h2>
        <div className="mh-nudge-cols">
          <div>
            <p className="mh-nudge-label">You’ve got</p>
            <ul className="mh-list mh-list-yes">
              <li><Check size={18} aria-hidden="true" /> Club posts &amp; studio updates</li>
              <li><Check size={18} aria-hidden="true" /> Thread Friend–only posts</li>
              <li><Check size={18} aria-hidden="true" /> Sharing makes &amp; cheering others on</li>
              <li><Heart size={18} aria-hidden="true" /> Keeping the free YouTube lives going</li>
            </ul>
          </div>
          <div>
            <p className="mh-nudge-label">Members also get</p>
            <ul className="mh-list mh-list-lock">
              <li><Lock size={16} aria-hidden="true" /> Two live classes a month</li>
              <li><Lock size={16} aria-hidden="true" /> Every replay, with chapters</li>
              <li><Lock size={16} aria-hidden="true" /> Member discounts on kits</li>
            </ul>
          </div>
        </div>
        <div className="mh-nudge-foot">
          <JoinButton variant="ink" />
          <p>Your {PLANS.friend.per.replace("/mo", "")} stops when you upgrade. No double billing.</p>
        </div>
      </motion.div>
    </div>
  </section>;
}

/* ═══ Up next: live class + this month's project ═══ */
function Countdown({ start }: { start: Date }) {
  const now = useNow(1000);
  const ms = Math.max(0, start.getTime() - now.getTime());
  const d = Math.floor(ms / 864e5), h = Math.floor(ms % 864e5 / 36e5), m = Math.floor(ms % 36e5 / 6e4), s = Math.floor(ms % 6e4 / 1e3);
  const parts = d > 0 ? [[d, "days"], [h, "hrs"], [m, "min"]] : [[h, "hrs"], [m, "min"], [s, "sec"]];
  const spoken = d > 0 ? `${d} days, ${h} hours` : `${h} hours, ${m} minutes`;
  return <div className="mh-count" role="timer" aria-label={`Starts in ${spoken}`}>
    {parts.map(([n, l]) => <span key={l as string}><b>{String(n).padStart(2, "0")}</b><small>{l}</small></span>)}
  </div>;
}

function NextLive({ lives, now }: { lives: Live[]; now: Date }) {
  const role = useRole();
  const to = useTo();
  const open = can(role, "live");
  const next = lives[0];
  if (!next) return null;
  const liveNow = now >= next.start && now.getTime() < next.start.getTime() + LIVE_MS;
  const project = PROJECTS[0];
  const thisProject = datePart(next.start, { month: "long", year: "numeric" }) === project.month;
  const { nights, n } = classNights(next);
  return <motion.article className={`mh-live${open ? "" : " is-locked"}`} aria-labelledby="mh-live-title"
    initial={{ opacity: 0, y: 40, rotate: 2 }} whileInView={{ opacity: 1, y: 0, rotate: -1.2 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 16 }}>
    <div className="mh-live-top">
      <p className="mh-live-kicker">{liveNow ? <span className="mh-onair"><i aria-hidden="true" /> Live now</span> : <><Radio size={15} aria-hidden="true" /> Next club class</>}</p>
      <p className="mh-live-when">{dayWord(next.start, now)}!</p>
    </div>
    <h3 id="mh-live-title" className="mh-live-title">{thisProject ? project.title : "Next month’s project"}<span>{thisProject ? `Class ${n} of ${nights.length}` : "announced soon"}</span></h3>
    <p className="mh-live-date">{longDate(next.start)} · 7 PM MT</p>
    {liveNow ? <p className="mh-live-now">{role === "chelsea" ? "You’re on air! The club is in the room." : "Chelsea’s live in the studio right now. Come say hi!"}</p> : <Countdown start={next.start} />}
    {nights.length > 1 && <ol className="mh-live-pair" aria-label={`${datePart(next.start, { month: "long" })} class nights`}>
      {nights.map((l, i) => { const done = l.start.getTime() + LIVE_MS <= now.getTime();
        return <li key={l.start.toISOString()} className={done ? "is-done" : undefined}>
          <b>{done ? <Check size={16} aria-hidden="true" /> : datePart(l.start, { day: "numeric" })}</b>
          <span>{datePart(l.start, { weekday: "short" })}{done ? ` ${datePart(l.start, { day: "numeric" })}` : ""} · class {i + 1}{done ? <span className="mh-sr">, done</span> : null}</span>
        </li>; })}
    </ol>}
    {open ? <div className="mh-live-actions">
      <Link to={to(M.live)} className="mb-btn mb-btn-pop">{role === "chelsea" ? "Open the studio" : liveNow ? "Join the class" : "Go to the live room"} <ArrowRight size={16} aria-hidden="true" /></Link>
      <p>{role === "chelsea" ? "Members get a reminder an hour before." : "Can’t make it? The replay is up the same night."}</p>
    </div> : <Locked need="members" what="live" className="mh-live-lock" />}
    <svg className="mh-live-squiggle" viewBox="0 0 120 40" aria-hidden="true"><Ink d="M4 30 C 20 4, 34 4, 40 20 S 62 36, 72 18 S 98 2, 116 14" delay={.5} width={3} /></svg>
  </motion.article>;
}

function ProjectCard() {
  const role = useRole();
  const to = useTo();
  const p = PROJECTS[0];
  const [got, setGot] = useState<boolean[]>(() => p.supplies.map((_, i) => i < 2 && role === "member"));
  const n = got.filter(Boolean).length;
  const open = can(role, "classes");
  const join = joinCta(role, "members");
  const firstClass = CLASSES.find(c => c.projectId === p.id);
  return <motion.article className="mh-proj" aria-labelledby="mh-proj-title"
    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: .1, type: "spring", stiffness: 120, damping: 16 }}>
    <figure className="mh-proj-snap">
      <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
      <span><img src={p.image} alt={`Sample of the ${p.title}: a colorful quilted patchwork bag`} /></span>
      <figcaption>{p.month.split(" ")[0]}’s make <b aria-hidden="true">♥</b></figcaption>
    </figure>
    <div className="mh-proj-body">
      <p className="mh-eyebrow">This month’s project · {p.skill}</p>
      <h3 id="mh-proj-title" className="mh-proj-title">{p.title}</h3>
      <p className="mh-proj-blurb">{p.blurb}</p>
      <fieldset className="mh-supplies">
        <legend>Supply list <span aria-live="polite">{n} of {p.supplies.length} gathered</span></legend>
        <ul>{p.supplies.map((s, i) => <li key={s}>
          <label><input type="checkbox" checked={got[i]} onChange={() => setGot(g => g.map((v, j) => (j === i ? !v : v)))} />
            <svg viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="2" width="22" height="22" rx="5" />{got[i] && <motion.path d="M7 13 L11.5 17.5 L19.5 8" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: .25 }} />}</svg>
            <span>{s}</span></label>
        </li>)}</ul>
      </fieldset>
      {p.pattern && <p className="mh-proj-pattern">Pattern: {p.pattern}</p>}
      {open || !join ? <Link to={to(firstClass ? M.klass(firstClass.id) : M.classes)} className="mh-arrowlink">
        Open the class page <ArrowRight size={16} aria-hidden="true" />
      </Link> : <LockedRow need="members" className="mh-proj-lock" />}
    </div>
  </motion.article>;
}

function UpNext({ lives, now }: { lives: Live[]; now: Date }) {
  const role = useRole();
  return <section id="mh-upnext" className="mh-sec mh-upnext" aria-labelledby="mh-upnext-title">
    <div className="b2-wrap">
      <Head id="mh-upnext-title" hand={role === "visitor" ? "here’s what’s" : "coming"} word={role === "visitor" ? "inside" : "up next"} tone="blue" className="mb-headsm" />
      <div className="mh-upnext-grid">
        <NextLive lives={lives} now={now} />
        <ProjectCard />
      </div>
    </div>
  </section>;
}

/* ═══ Continue watching ═══ */
function ReplayCard({ c, i, chelsea, fresh }: { c: ClassVideo; i: number; chelsea: boolean; fresh?: boolean }) {
  const to = useTo();
  const total = secs(c.length), at = total * (c.watched ?? 0);
  const project = PROJECTS.find(p => p.id === c.projectId);
  return <motion.li initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * .08, duration: .5, ease: EASE }}>
    <Link to={to(M.klass(c.id))} className="mh-replay">
      <span className="mh-replay-img">
        <img src={c.poster} alt="" loading="lazy" />
        <b><Play size={20} fill="currentColor" aria-hidden="true" /></b>
        <em>{c.length}</em>
      </span>
      <span className="mh-replay-meta">{project?.title} · {c.part}</span>
      <span className="mh-replay-title">{c.title}</span>
      {chelsea ? <span className="mh-replay-sub">{fresh ? <><Sparkles size={14} aria-hidden="true" /> Just published · members notified</> : <><BarChart3 size={14} aria-hidden="true" /> {REPLAY_VIEWS[c.id] ? `${REPLAY_VIEWS[c.id]} views · sample` : "No views yet"}</>}</span>
        : <>
          <span className="mh-progress" role="progressbar" aria-label={`Watched ${Math.round((c.watched ?? 0) * 100)}%`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((c.watched ?? 0) * 100)}><i style={{ width: `${(c.watched ?? 0) * 100}%` }} /></span>
          <span className="mh-replay-sub">Resume at {clock(at)} · {Math.round((total - at) / 60)} min left</span>
        </>}
    </Link>
  </motion.li>;
}

function Continue({ now, published }: { now: Date; published: string[] }) {
  const role = useRole();
  const to = useTo();
  const chelsea = role === "chelsea";
  const library = CLASSES.filter(c => hasReplay(c, now) || published.includes(c.id)).sort(byNewest);
  const list = chelsea ? [...library].sort((a, b) => Number(published.includes(b.id)) - Number(published.includes(a.id))).slice(0, 3)
    : library.filter(c => (c.watched ?? 0) > 0 && (c.watched ?? 0) < 1);
  const more = library.length - list.length;
  const open = can(role, "classes");
  return <section className="mh-sec mh-watch" aria-labelledby="mh-watch-title">
    <div className="b2-wrap">
      <div className="mh-row">
        <Head id="mh-watch-title" hand={chelsea ? "latest" : open ? "pick up where you" : "every class has a"} word={chelsea ? "replays" : open ? "left off" : "replay"} tone="blue" className="mb-headsm" />
        <Link to={to(M.classes)} className="mh-arrowlink">{open ? "All classes" : "Browse the library"} <ArrowRight size={16} aria-hidden="true" /></Link>
      </div>
      {open ? (list.length ? <ul className="mh-replays">
        {list.map((c, i) => <ReplayCard key={c.id} c={c} i={i} chelsea={chelsea} fresh={published.includes(c.id)} />)}
        {!chelsea && list.length < 3 && more > 0 && <li className="mh-replay-more-li"><Link to={to(M.classes)} className="mh-replay-more">
          <Play size={22} fill="currentColor" aria-hidden="true" />
          <span className="mh-replay-more-n">{more} more</span>
          <span>replays in the library, every one with chapters</span>
          <span className="mh-arrowlink">Browse classes <ArrowRight size={16} aria-hidden="true" /></span>
        </Link></li>}
      </ul>
        : <p className="mh-empty b2-note">All caught up! ♥</p>)
        : <Locked need="members" what="replays" className="mh-watch-lock"
          peek={<ul className="mh-replays">
            {replays(now).slice(0, 3).map(c => <li key={c.id}><span className="mh-replay"><span className="mh-replay-img"><img src={c.poster} alt="" loading="lazy" /></span><span className="mh-replay-title">{c.title}</span></span></li>)}
          </ul>} />}
    </div>
  </section>;
}

/* ═══ Latest posts ═══ */
function LikeButton({ base, label }: { base: number; label: string }) {
  const [on, setOn] = useState(false);
  return <button type="button" className="mh-like" aria-pressed={on} aria-label={`Like ${label}`} onClick={() => setOn(v => !v)}>
    <Heart size={16} fill={on ? "currentColor" : "none"} aria-hidden="true" /> <span aria-live="polite">{base + (on ? 1 : 0)}</span>
  </button>;
}

function Poll({ poll }: { poll: NonNullable<Post["poll"]> }) {
  const [pick, setPick] = useState<number | null>(null);
  const total = poll.options.reduce((a, o) => a + o.votes, 0) + (pick === null ? 0 : 1);
  return <div className="mh-poll" role="group" aria-label={poll.q}>
    {poll.options.map((o, i) => {
      const v = o.votes + (pick === i ? 1 : 0), pct = Math.round(v / total * 100);
      return <button key={o.label} type="button" className="mh-poll-opt" aria-pressed={pick === i} onClick={() => setPick(pick === i ? null : i)}>
        {pick !== null && <i style={{ width: `${pct}%` }} aria-hidden="true" />}
        <span>{o.label}</span>{pick !== null && <b>{pct}%</b>}
      </button>;
    })}
    <p className="mh-poll-n" aria-live="polite">{total} votes{pick !== null ? " · thanks for voting!" : " · tap to vote"}</p>
  </div>;
}

function PostCard({ p, i, big }: { p: Post; i: number; big?: boolean }) {
  const role = useRole();
  const to = useTo();
  const open = canRead(role, p);
  const isNew = p.id.startsWith("new-");
  const comments = countComments(p, role === "chelsea");
  const need = postNeed(p);
  const href = isNew ? to(M.posts) : open || !need ? to(M.post(p.id)) : joinCta(role, need)?.to ?? to(M.join);
  return <motion.article className={`mh-post${big ? " is-big" : ""}${open ? "" : " is-locked"}`} aria-labelledby={`mh-post-${p.id}`}
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * .08, duration: .5, ease: EASE }}>
    {big && p.images?.[0] && open && <Link to={href} className="mh-post-img" tabIndex={-1} aria-hidden="true"><img src={p.images[0]} alt="" loading="lazy" /></Link>}
    <div className="mh-post-body">
      <div className="mh-post-by">
        <Avatar name="Chelsea" src={CHELSEA.avatar} size={34} />
        <span><b>Chelsea</b> · {p.ago}</span>
        {p.pinned && <span className="mh-pin"><Pin size={12} aria-hidden="true" /> Pinned</span>}
        <VisBadge visibility={p.visibility} size="sm" className="mh-vis" />
      </div>
      <h3 id={`mh-post-${p.id}`} className="mh-post-title"><Link to={href}>{p.title}</Link></h3>
      {open ? <>
        {p.body && <p className="mh-post-text">{p.body}</p>}
        {p.poll && <Poll poll={p.poll} />}
        <div className="mh-post-foot">
          <LikeButton base={p.likes} label={p.title} />
          <Link to={href} className="mh-post-comments"><MessageCircle size={16} aria-hidden="true" /> {comments} {comments === 1 ? "comment" : "comments"}</Link>
        </div>
      </> : <div className="mh-post-locked">
        <p className="mh-post-blur" aria-hidden="true">{LOCKED_FILLER}</p>
        {need && <LockedRow need={need} className="mh-post-why" />}
      </div>}
    </div>
  </motion.article>;
}

function Posts({ posts }: { posts: Post[] }) {
  const to = useTo();
  const list = useMemo(() => [...posts].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned)).slice(0, 4), [posts]);
  return <section className="mh-sec mh-posts" aria-labelledby="mh-posts-title">
    <div className="b2-wrap">
      <div className="mh-row">
        <Head id="mh-posts-title" hand="from the" word="studio" tone="pink" className="mb-headsm" />
        <Link to={to(M.posts)} className="mh-arrowlink">All posts <ArrowRight size={16} aria-hidden="true" /></Link>
      </div>
      <div className="mh-posts-grid">
        {list[0] && <PostCard p={list[0]} i={0} big />}
        <div className="mh-posts-side">{list.slice(1).map((p, i) => <PostCard key={p.id} p={p} i={i + 1} />)}</div>
      </div>
    </div>
  </section>;
}

/* ═══ Makes + challenge ═══ */
function Makes() {
  const role = useRole();
  const to = useTo();
  const open = can(role, "community");
  return <section className="mh-sec mh-makes" aria-labelledby="mh-makes-title">
    <div className="b2-wrap">
      <div className="mh-row">
        <Head id="mh-makes-title" hand="look what you" word="made" tone="blue" className="mb-headsm" />
        <Link to={to(M.community)} className="mh-arrowlink">All makes <ArrowRight size={16} aria-hidden="true" /></Link>
      </div>
      <div className="mh-makes-grid">
        <motion.aside className="mh-challenge" aria-labelledby="mh-chal-title" initial={{ opacity: 0, y: 30, rotate: -4 }} whileInView={{ opacity: 1, y: 0, rotate: -2 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 15 }}>
          <i className="b2-snap-tape b2-ktape-extra" aria-hidden="true" />
          <p className="mh-eyebrow">This month’s challenge</p>
          <h3 id="mh-chal-title">{CHALLENGE.title}</h3>
          <p>{CHALLENGE.blurb}</p>
          <p className="mh-chal-meta"><b>{CHALLENGE.entries}</b> entries · ends {CHALLENGE.ends}</p>
          {open ? <Link to={to(M.community)} className="mb-btn mb-btn-ink">{role === "chelsea" ? "See the entries" : "Share a make"} <ArrowRight size={16} aria-hidden="true" /></Link>
            : <JoinButton need="friends" variant="ink" className="mh-chal-join" />}
          <svg className="mh-chal-star" viewBox="0 0 60 60" aria-hidden="true"><Ink d="M30 6 L36 23 L54 24 L40 35 L45 53 L30 43 L15 53 L20 35 L6 24 L24 23 Z" delay={.6} width={2.6} /></svg>
        </motion.aside>
        <div className="mh-strip-wrap">
          <ul className="mh-strip" aria-label="Recent makes" tabIndex={0}>
            {MAKES.map((k, i) => <motion.li key={k.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * .06, duration: .45, ease: EASE }} style={{ "--tilt": `${[-2.2, 1.6, -1, 2.2, -1.8, 1.2][i]}deg` } as CSSProperties}>
              <figure className="mh-make">
                <i className={`b2-snap-tape b2-ktape-${TAPES[i]}`} aria-hidden="true" />
                <span className="mh-make-img"><img src={k.image} alt={k.title} loading="lazy" />{k.challenge && <em>{k.challenge}</em>}</span>
                <figcaption>
                  <span className="mh-make-title">{k.title}</span>
                  <span className="mh-make-by"><Avatar name={k.by.name} size={22} /> {k.by.name}</span>
                  {open ? <LikeButton base={k.likes} label={`${k.title} by ${k.by.name}`} /> : <span className="mh-like is-static"><Heart size={15} aria-hidden="true" /> {k.likes}</span>}
                </figcaption>
              </figure>
            </motion.li>)}
          </ul>
          <p className="mh-strip-hint" aria-hidden="true">scroll for more →</p>
        </div>
      </div>
    </div>
  </section>;
}

/* ═══ Free on YouTube ═══ */
function YouTubeTeaser({ now }: { now: Date }) {
  const free = upcomingLives(2, "youtube", now);
  return <section className="mh-sec mh-yt" aria-labelledby="mh-yt-title">
    <div className="b2-wrap mh-yt-grid">
      <div>
        <Head id="mh-yt-title" hand="free for everyone on" word="Tuesdays" tone="blue" className="mb-headsm" />
        <p className="mh-yt-lede">Most Tuesdays I’m live on YouTube at 7 PM Mountain, free for anyone. Bring a friend who isn’t in the club yet!</p>
        <a href={YOUTUBE_LIVE_URL} target="_blank" rel="noreferrer" className="mb-btn mh-btn-live"><Youtube size={18} aria-hidden="true" /> Watch on YouTube</a>
      </div>
      <ul className="mh-yt-dates" aria-label="Next free YouTube lives">
        {free.map((l, i) => <motion.li key={l.start.toISOString()} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0, rotate: [-3, 2.5][i] }} viewport={VIEW} transition={{ delay: .2 + i * .12, duration: .5, ease: EASE }}>
          <a href={l.url} target="_blank" rel="noreferrer" aria-label={`${longDate(l.start)}, 7 PM Mountain, free live on YouTube`}>
            <small>{datePart(l.start, { weekday: "short" })}</small>
            <b>{datePart(l.start, { day: "numeric" })}</b>
            <span>{datePart(l.start, { month: "short" })} · 7 PM MT</span>
            <em><Play size={10} fill="currentColor" aria-hidden="true" /> YouTube</em>
          </a>
        </motion.li>)}
      </ul>
    </div>
  </section>;
}

/* ═══ Page ═══ */
function HomeBody({ desk, setDesk, mine, onPost, toast }: { desk: Desk; setDesk: (d: Desk) => void; mine: Post[]; onPost: (p: Post) => void; toast: (m: string) => void }) {
  const role = useRole();
  const now = useNow(30000);
  const [published, setPublished] = useState<string[]>([]);
  const lives = upcomingLives(2, "club", now);
  const month = datePart(now, { month: "numeric", year: "numeric" });
  const defaults: Defaults = {
    club: isoDenver((upcomingLives(4, "club", now).find(l => datePart(l.start, { month: "numeric", year: "numeric" }) !== month) ?? lives[0])?.start ?? now),
    youtube: isoDenver(upcomingLives(1, "youtube", now)[0]?.start ?? now),
    today: isoDenver(now),
  };
  // Hide "replay is up" posts for classes that haven't aired yet (see hasReplay()).
  const posts = useMemo(() => [...mine, ...POSTS].filter(p => { const c = p.classId && CLASSES.find(x => x.id === p.classId); return !c || hasReplay(c, now); }), [mine, now]);
  return <>
    {role === "chelsea" && <Part p="where"><StudioDesk open={desk} setOpen={setDesk} onPost={onPost} onPublish={id => setPublished(l => (l.includes(id) ? l : [id, ...l]))} toast={toast} defaults={defaults} now={now} /></Part>}
    {role === "friend" && <Part p="making"><FriendNudge /></Part>}
    <Part p="club"><UpNext lives={lives} now={now} /></Part>
    <Part p="making"><Continue now={now} published={published} /></Part>
    <Part p="club"><Posts posts={posts} /></Part>
    <Part p="words"><Makes /></Part>
    <Part p="hero"><YouTubeTeaser now={now} /></Part>
  </>;
}

/** Club home: the member's dashboard. The role comes from MembersPage's
    context, so everything that reads it renders inside the page. */
export function MembersHome() {
  const { toast, show } = useToast();
  const [desk, setDesk] = useState<Desk>(null);
  const [mine, setMine] = useState<Post[]>([]);
  return <MotionConfig reducedMotion="user">
    <MembersPage page="home" header={<Header openDesk={setDesk} />}>
      <HomeBody desk={desk} setDesk={setDesk} mine={mine} onPost={p => setMine(m => [p, ...m])} toast={show} />
      <Toast toast={toast} />
    </MembersPage>
  </MotionConfig>;
}
